// @ts-strict-ignore
import {
  Calculation,
  CalculationTypes,
  Entry,
  isQueryUserEntry,
  QueryEntry,
  QueryMetricEntry,
  SelectorTypeAggregation,
} from 'common/query-builder/query-entry-section/types';
import {AggregationsMappings} from 'common/query-builder/query-entry-section/constants';
import {pluralize, toTitleCase} from 'common/util/string';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {AggregationGroupLabel, SelectorTypeAggregationLabel} from 'common/query-builder/types/shared-types';
import {RollingWindowSize} from 'common/util';
import {DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {parseAndFormatDate} from 'common/util/date';
import {DateRangeType, TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
} from 'common/widgets/date-range-picker/models/presets';
import {ChartType} from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';
import {isScdPropertyName} from 'common/report/util/properties';
import {RECURRING_REVENUE_CHANGE} from 'common/query-builder/query-block/properties-menu/constants';

export function getSelectedGroupLabel({
  dataGroupId,
  dataGroups,
  isPlural = false,
}: {
  dataGroupId: string;
  dataGroups: DataGroup[];
  isPlural?: boolean;
}): Translatable {
  if (!dataGroupId) {
    return ({t}) => t(`query-builder.dg-user`, {count: isPlural ? 2 : 1});
  } else {
    const dataGroup = dataGroups.find((group) => group.groupID === dataGroupId);
    if (dataGroup) {
      // todo: pluralize not supported with i18n
      return pluralize(dataGroup.displayName, isPlural, null, `(s)`);
    }

    return ({t}) => t(`query-builder.dg-group`, {count: isPlural ? 2 : 1});
  }
}

/**
 * Formats a given label to include the selected data group (Users, Accounts, etc...)
 */
export function formatDataGroupLabel({
  label,
  dataGroupId,
  dataGroups,
}: {
  label: SelectorTypeAggregationLabel;
  dataGroupId: string;
  dataGroups: DataGroup[];
}) {
  const commonProps = {dataGroupId, dataGroups};

  return label({
    [AggregationGroupLabel.Group]: getSelectedGroupLabel(commonProps),
    [AggregationGroupLabel.GroupTitle]: ({print}) => toTitleCase(print(getSelectedGroupLabel(commonProps))),
    [AggregationGroupLabel.PluralGroup]: getSelectedGroupLabel({...commonProps, isPlural: true}),
    [AggregationGroupLabel.PluralGroupTitle]: ({print}) =>
      toTitleCase(print(getSelectedGroupLabel({...commonProps, isPlural: true}))),
    [AggregationGroupLabel.MetricType]: null, // Not needed for DataGroupLabels
  });
}

/**
 * Formats the report date range in a human friendly format for use in query entry section selectors.
 * Label is generally a prepositional phrase added to examples like "First time " or "Last time "
 */
export function getSelectedTimePeriodLabel(
  dateRange: DateRangeAttributes,
  chartType: ChartType,
  dateUnit: RollingWindowSize,
) {
  const FALLBACK_LABEL = `in time period`;
  if (!dateRange) {
    return FALLBACK_LABEL;
  }

  if ([ChartType.Line, ChartType.Column].includes(chartType)) {
    return `each ${dateUnit}`;
  }

  // Handle special cases of 'between' up front
  if (dateRange.from === START_OF_TODAY && dateRange.to === END_OF_TODAY) {
    return `for today`;
  } else if (dateRange.from === START_OF_YESTERDAY && dateRange.to === END_OF_YESTERDAY) {
    return `for yesterday`;
  }

  const fromDateLabel = parseAndFormatDate(dateRange.from);
  const toDateLabel = parseAndFormatDate(dateRange.to);

  switch (dateRange.type) {
    case DateRangeType.RelativeAfter:
      if (dateRange.window?.value && dateRange.window?.unit) {
        return `for the past ${dateRange.window.value} ${pluralize(dateRange.window.unit, dateRange.window.value)}`;
      }
      break;

    case DateRangeType.Since:
      if (fromDateLabel) {
        return `since ${fromDateLabel}`;
      }
      break;

    case DateRangeType.Between:
      if (fromDateLabel && toDateLabel) {
        return `between ${fromDateLabel} - ${toDateLabel}`;
      }
      break;
  }
  return FALLBACK_LABEL;
}

/**
 * Returns the top level aggregation for a given entry
 */
function getSelectedAggregationForEntry(entry: QueryMetricEntry): SelectorTypeAggregation {
  const hasPerUserAggregator = Boolean(entry.aggregationOperatorPerUser);
  const searchCriteria = {
    forProperty: Boolean(entry.property),
    /** `operator` defines the second level aggregation (across all users/events), since
     *  we are looking for the top level aggregation, we only pass one of `operator` or `aggregationOperatorPerUser`
     */
    operator: hasPerUserAggregator ? null : entry.aggregationOperator,
    perUserOperator: entry.aggregationOperatorPerUser ?? null,
  };

  for (const aggregation of AggregationsMappings.get(entry.type).values()) {
    if (
      (aggregation.forProperty ?? false) === searchCriteria.forProperty &&
      (aggregation.operator ?? null) === searchCriteria.operator &&
      (aggregation.perUserOperator ?? null) === searchCriteria.perUserOperator
    ) {
      return aggregation;
    }
  }

  return null;
}

function getAggregationLabel({
  aggregation,
  dataGroupId,
  dataGroups,
}: {
  aggregation: SelectorTypeAggregation;
  dataGroupId: string;
  dataGroups: DataGroup[];
}) {
  return formatDataGroupLabel({
    label: aggregation?.shortLabel ?? aggregation.label,
    dataGroupId,
    dataGroups,
  });
}

/**
 * Returns the sub level aggregation for a given entry
 */
function getSelectedSubAggregationForEntry(entry: QueryMetricEntry) {
  const subAggregations = getSelectedAggregationForEntry(entry)?.subAggregations ?? [];
  return subAggregations.find((aggregation) => aggregation.operator === entry.aggregationOperator);
}

/**
 * Returns selected math function for a given entry
 */
function getSelectorAggregationLabelForEntry({
  entry,
  dataGroupId,
  dataGroups,
  forNewWorkflow,
}: {
  entry: QueryMetricEntry;
  dataGroupId: string;
  dataGroups: DataGroup[];
  forNewWorkflow: boolean;
}) {
  const aggregation = getSelectedAggregationForEntry(entry);
  let label = getAggregationLabel({aggregation, dataGroupId, dataGroups});

  // for the sidebar QB we display `Count Total` as a top level aggregation selection
  if (forNewWorkflow) {
    const addPrefix = !aggregation.forProperty && !/(aggregate|count)/i.test(label.toString());
    label = addPrefix ? `Count ${label}` : label;
  }

  return label;
}

export function getSelectedAggregationData({
  entry,
  dataGroupId,
  dataGroups,
  forNewWorkflow,
}: {
  entry: QueryMetricEntry;
  dataGroupId: string;
  dataGroups: DataGroup[];
  forNewWorkflow: boolean;
}) {
  // Distinct Count per user is an example of a first pass aggregation
  const firstPassAggregation = getSelectedAggregationForEntry(entry);
  const firstPassAggregationLabel = getSelectorAggregationLabelForEntry({
    entry,
    forNewWorkflow,
    dataGroupId,
    dataGroups,
  });

  // By Distribution is an example of a second pass aggregation
  const secondPassAggregation = getSelectedSubAggregationForEntry(entry);
  const secondPassAggregationLabel =
    secondPassAggregation && getAggregationLabel({aggregation: secondPassAggregation, dataGroupId, dataGroups});

  return {
    firstPassAggregation,
    secondPassAggregation,
    firstPassAggregationLabel,
    secondPassAggregationLabel,
  };
}

export function getSelectedCalculation({
  cumulative,
  rolling,
}: Pick<QueryMetricEntry, `cumulative` | `rolling`>): Calculation {
  let calculationType: CalculationTypes = null;
  let rollingWindowSize: number = null;

  if (cumulative) {
    calculationType = CalculationTypes.Cumulative;
  } else if (rolling) {
    calculationType = CalculationTypes.Rolling;
    rollingWindowSize = rolling.rollingWindowSize;
  }

  return {
    calculationType,
    ...(rollingWindowSize !== null ? {rollingWindowSize} : {}),
  };
}

export function getQueryEntryUpdateFromCalculation({
  originalCalculation,
  calculation,
}: {
  originalCalculation: Pick<QueryMetricEntry, `cumulative` | `rolling`>;
  calculation: Calculation;
}): Pick<QueryEntry, `rolling` | `cumulative`> {
  let rolling = originalCalculation.rolling;
  let cumulative = Boolean(originalCalculation.cumulative);
  if (calculation.calculationType === CalculationTypes.Rolling) {
    rolling = {rollingWindowSize: calculation.rollingWindowSize};
    cumulative = false;
  } else if (calculation.calculationType === CalculationTypes.Cumulative) {
    rolling = null;
    cumulative = true;
  }

  return {
    rolling,
    cumulative,
  };
}

export function getSelectedCalculationLabel(calculation: Calculation, dateUnit: RollingWindowSize): Translatable {
  const {calculationType, rollingWindowSize} = calculation;
  let selectedLabel: Translatable;
  switch (calculationType) {
    case CalculationTypes.Cumulative:
      selectedLabel = ({t}) => t(`query-builder.count-menu.calculations.cumulative-sum`);
      break;
    case CalculationTypes.Rolling:
      selectedLabel = ({t, print}) =>
        `${t(`query-builder.count-menu.calculations.rolling-avg`)} - ${t(`reports.time-unit.x-units`, {
          count: rollingWindowSize,
          timeUnit: print(TimeUnitLabelTranslatable[dateUnit](rollingWindowSize)),
        })}`;
      break;
  }

  return selectedLabel;
}

export function isProfileScdAggregation(entry: Entry) {
  if (!isQueryUserEntry(entry)) {
    return false;
  }
  if (!entry.property) {
    return false;
  }
  return isScdPropertyName(entry.property.value) || entry.property.value === RECURRING_REVENUE_CHANGE.value;
}
